"use client";

import { addDays, endOfDay, format, startOfDay } from "date-fns";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { ReactNode, useState } from "react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import * as React from "react";

type DatePickerProps = {
  className?: string;
  range?: { from: number; to?: number };
  canSelectRange?: boolean;
  onSelect?: (date: { from?: number; to?: number }) => void;
  onRemove?: () => void;
  customButton?: ReactNode;
  clearable?: boolean;
  clearLabel?: string;
  placeholderText?: string;
  numberOfMonths?: number;
  closeOnSelect?: boolean;
};

type DateTriggerProps = React.ComponentPropsWithoutRef<"div"> & {
  value: boolean | number | number[] | string | string[];
  placeholder: string;
  className?: string;
};

export const DateTrigger = React.forwardRef<HTMLDivElement, DateTriggerProps>(
  ({ value, placeholder, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        tabIndex={0}
        role="button"
        {...props}
        className={cn(
          "flex items-center h-8 pl-2 pr-2 text-sm cursor-default relative z-50 max-h-96 min-w-[8rem]",
          "overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {Array.isArray(value) && value[0] ? (
          value[1] ? (
            <>
              {format(value[0], "LLL dd, y")} — {format(value[1], "LLL dd, y")}
            </>
          ) : (
            format(value[0], "LLL dd, y")
          )
        ) : typeof value === "number" ? (
          format(value, "LLL dd, y")
        ) : (
          <span>{placeholder}</span>
        )}
      </div>
    );
  },
);

DateTrigger.displayName = "DateTrigger";

export function DateRangePicker({
  className,
  range = {
    from: startOfDay(addDays(new Date(2022, 0, 20), 20)).getTime(),
    to: endOfDay(Date.now()).getTime(),
  },
  canSelectRange = true,
  onSelect,
  onRemove,
  customButton,
  clearable = false,
  clearLabel = "Effacer",
  placeholderText = "Saisir la période",
  numberOfMonths = 2,
  closeOnSelect = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>({
    from: range.from ? startOfDay(new Date(range.from)) : undefined,
    to: range.to ? endOfDay(new Date(range.to)) : undefined,
  });

  const _onDayClick = (day: Date) => {
    if (canSelectRange && selectedRange?.from && !selectedRange.to) {
      const fromTimestamp = startOfDay(selectedRange.from).getTime();
      const toTimestamp = endOfDay(day).getTime();
      const fromTime = Math.min(fromTimestamp, toTimestamp);
      const toTime = Math.max(fromTimestamp, toTimestamp);

      setSelectedRange({ from: new Date(fromTime), to: new Date(toTime) });
      onSelect?.({ from: fromTime, to: toTime });
      if (closeOnSelect) {
        setOpen(false);
      }
    } else {
      const fromTime = startOfDay(day).getTime();
      setSelectedRange({ from: new Date(fromTime), to: undefined });
      onSelect?.({ from: fromTime, to: undefined });
      if (!canSelectRange && closeOnSelect) {
        setOpen(false);
      }
    }
  };

  const _onClear = () => {
    setSelectedRange(undefined);
    onSelect?.({ from: undefined, to: undefined });
    onRemove?.();
  };

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {customButton ? (
            customButton
          ) : (
            <Button
              id="date"
              variant="outline"
              // pr-9 = espace pour le bouton Clear
              className={cn(
                "w-full justify-start text-left font-normal pr-9",
                !selectedRange && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedRange?.from ? (
                selectedRange.to ? (
                  <>
                    {format(selectedRange.from, "LLL dd, y")} —{" "}
                    {format(selectedRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(selectedRange.from, "LLL dd, y")
                )
              ) : (
                <span>{placeholderText}</span>
              )}
            </Button>
          )}
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0"
          align="start"
          updatePositionStrategy="optimized"
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={selectedRange?.to}
            selected={selectedRange}
            onDayClick={_onDayClick}
            numberOfMonths={numberOfMonths}
          />
        </PopoverContent>
      </Popover>

      {clearable && selectedRange && (
        <button
          type="button"
          onClick={_onClear}
          // empêcher l’ouverture du popover en cliquant sur Clear
          onPointerDown={(e) => e.preventDefault()}
          aria-label={clearLabel}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
